import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft, ChevronRight, UploadCloud } from "lucide-react";

export default function CandidateDatabase() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const limit = 25;

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/candidates?search=${search}&limit=${limit}&offset=${page * limit}`);
      setCandidates(res.data.candidates);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCandidates(); }, [page, api]);

  const handleSearch = (e) => { e.preventDefault(); setPage(0); loadCandidates(); };

  if (selected) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="candidate-detail">
        <Button variant="ghost" onClick={() => setSelected(null)} className="mb-4 text-slate-500">&larr; Back</Button>
        <Card><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-xl font-heading text-slate-900">{selected.first_name} {selected.last_name}</h2>
            <Badge className="font-mono text-xs bg-slate-100 text-slate-600 border-0">{selected.anonymous_id}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[
              ["Current Title", selected.current_title], ["Seniority", selected.seniority_level],
              ["Functional Area", selected.functional_area], ["Employer", selected.current_employer_name],
              ["Employer Category", selected.current_employer_category], ["Employer Domain", selected.current_employer_domain],
              ["Geography", selected.geography_region], ["Degree", selected.highest_degree],
              ["LinkedIn URL", selected.linkedin_url], ["Remote Willing", selected.remote_willing ? "Yes" : "No"],
            ].map(([label, val]) => val && (
              <div key={label}><span className="text-xs font-mono uppercase tracking-widest text-slate-400 block">{label}</span><span className="text-slate-900">{val}</span></div>
            ))}
          </div>
          {selected.therapeutic_areas && (
            <div className="mt-4"><span className="text-xs font-mono uppercase tracking-widest text-slate-400 block mb-2">Therapeutic Areas</span>
              <div className="flex flex-wrap gap-1.5">{selected.therapeutic_areas.map(ta => <Badge key={ta} className="text-xs bg-teal-50 text-teal-700 border border-teal-200">{ta}</Badge>)}</div></div>
          )}
          {selected.ai_experience_summary && <div className="mt-4"><span className="text-xs font-mono uppercase tracking-widest text-slate-400 block mb-2">AI Summary</span><p className="text-sm text-slate-600">{selected.ai_experience_summary}</p></div>}
          {selected.intent_level10_summary && <div className="mt-4"><span className="text-xs font-mono uppercase tracking-widest text-slate-400 block mb-2">Intent Profile</span><p className="text-sm text-slate-600">{selected.intent_level10_summary}</p></div>}
          {selected.linkedin_headline && <div className="mt-4"><span className="text-xs font-mono uppercase tracking-widest text-slate-400 block mb-2">LinkedIn Headline</span><p className="text-sm text-slate-500 italic">{selected.linkedin_headline}</p></div>}
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="candidate-database">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div><h1 className="text-2xl font-heading text-slate-900">Candidate Database</h1><p className="text-sm text-slate-500 mt-1">{total.toLocaleString()} candidates total</p></div>
        <Button data-testid="candidate-database-upload-button" onClick={() => navigate("/admin/candidate-upload")} className="bg-evergreen-950 hover:bg-evergreen-900 text-white">
          <UploadCloud className="w-4 h-4 mr-2" /> Upload Candidates
        </Button>
      </div>
      <form onSubmit={handleSearch} className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="candidate-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, title, ID..." className="pl-10" />
        </div>
        <Button type="submit" className="bg-evergreen-950 hover:bg-evergreen-900 text-white">Search</Button>
      </form>
      {loading ? <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-14 bg-white rounded-lg border animate-pulse" />)}</div> : (
        <>
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">ID</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Name</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Title</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Employer</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Seniority</th>
                <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">FA</th>
              </tr></thead>
              <tbody>
                {candidates.map(c => (
                  <tr key={c.anonymous_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setSelected(c)} data-testid={`cand-row-${c.anonymous_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{c.anonymous_id}</td>
                    <td className="px-4 py-3 text-slate-900">{c.first_name} {c.last_name}</td>
                    <td className="px-4 py-3 text-slate-600">{c.current_title}</td>
                    <td className="px-4 py-3 text-slate-600">{c.current_employer_name}</td>
                    <td className="px-4 py-3"><Badge variant="secondary" className="text-xs">{c.seniority_level || "—"}</Badge></td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{c.functional_area || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-slate-500">Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
